import { useState, useEffect, useCallback, useRef } from 'react';
import { Alert, AlertOverview, NotificationChannel, RoutingRule, MaintenanceWindow, AlertFilters, AlertsError } from '../types';
import { alertsService } from '../services/alertsService';

interface UseAlertsDataOptions {
  autoRefresh?: boolean;
  refreshInterval?: number;
  enableLiveUpdates?: boolean;
  initialFilters?: AlertFilters;
}

interface UseAlertsDataReturn {
  alerts: Alert[];
  overview: AlertOverview | null;
  notificationChannels: NotificationChannel[];
  routingRules: RoutingRule[];
  maintenanceWindows: MaintenanceWindow[];
  loading: boolean;
  error: AlertsError | null;
  filters: AlertFilters;
  setFilters: (filters: AlertFilters) => void;
  refetch: () => Promise<void>;
  lastUpdated: Date | null;
  // Action methods
  acknowledgeAlert: (alertId: string) => Promise<void>;
  suppressAlert: (alertId: string, duration: number) => Promise<void>;
  deleteAlert: (alertId: string) => Promise<void>;
}

export const useAlertsData = (
  options: UseAlertsDataOptions = {}
): UseAlertsDataReturn => {
  console.log('=== DEBUG: useAlertsData hook called ===', options);
  
  const {
    autoRefresh = true,
    refreshInterval = 30000, // 30 seconds for alerts
    enableLiveUpdates = true,
    initialFilters = {}
  } = options;

  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [overview, setOverview] = useState<AlertOverview | null>(null);
  const [notificationChannels, setNotificationChannels] = useState<NotificationChannel[]>([]);
  const [routingRules, setRoutingRules] = useState<RoutingRule[]>([]);
  const [maintenanceWindows, setMaintenanceWindows] = useState<MaintenanceWindow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AlertsError | null>(null);
  const [filters, setFilters] = useState<AlertFilters>(initialFilters);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const isMountedRef = useRef(true);
  const reconnectAttemptsRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;

  const fetchData = useCallback(async (): Promise<void> => {
    if (!isMountedRef.current) return;

    try {
      setLoading(true);
      setError(null);

      console.log('Alerts Tab: Starting data fetch with filters:', filters);

      const data = await alertsService.getAllAlerts(filters);
      
      console.log('Alerts Tab: Data received:', {
        alertsCount: data.alerts.length,
        firstAlert: data.alerts[0]?.name || 'No alerts',
        firstAlertId: data.alerts[0]?.id || 'No ID',
        activeAlerts: data.overview.activeAlarms,
        channelsCount: data.notificationChannels.length,
        rulesCount: data.routingRules.length,
        fullData: data
      });

      if (isMountedRef.current) {
        console.log('Alerts Tab: Setting state with alerts:', data.alerts.length, 'alerts');
        setAlerts(data.alerts);
        setOverview(data.overview);
        setNotificationChannels(data.notificationChannels);
        setRoutingRules(data.routingRules);
        setMaintenanceWindows(data.maintenanceWindows);
        setLastUpdated(new Date());
        console.log('Alerts Tab: State updated successfully');
      }

    } catch (error: any) {
      console.error('Alerts Tab: Error fetching data:', error);
      
      if (isMountedRef.current) {
        setError({
          code: error.code || 'FETCH_ERROR',
          message: error.message || 'Failed to fetch alerts data',
          service: 'alerts',
          timestamp: new Date().toISOString()
        });
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [filters]);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    if (autoRefresh && refreshInterval > 0) {
      refreshTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          fetchData();
        }
      }, refreshInterval);
    }
  }, [autoRefresh, refreshInterval, fetchData]);

  const refetch = useCallback(async (): Promise<void> => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    await fetchData();
    scheduleRefresh();
  }, [fetchData, scheduleRefresh]);

  // Alert action methods
  const acknowledgeAlert = useCallback(async (alertId: string): Promise<void> => {
    try {
      await alertsService.acknowledgeAlert(alertId);
      // Update local state
      setAlerts(prev => prev.map(alert => 
        alert.id === alertId 
          ? { ...alert, status: 'acknowledged' as const }
          : alert
      ));
    } catch (error: any) {
      console.error('Error acknowledging alert:', error);
      throw error;
    }
  }, []);

  const suppressAlert = useCallback(async (alertId: string, duration: number): Promise<void> => {
    try {
      await alertsService.suppressAlert(alertId, duration);
      // Update local state
      setAlerts(prev => prev.map(alert => 
        alert.id === alertId 
          ? { ...alert, status: 'suppressed' as const }
          : alert
      ));
    } catch (error: any) {
      console.error('Error suppressing alert:', error);
      throw error;
    }
  }, []);

  const deleteAlert = useCallback(async (alertId: string): Promise<void> => {
    try {
      await alertsService.deleteAlert(alertId);
      // Update local state
      setAlerts(prev => prev.filter(alert => alert.id !== alertId));
    } catch (error: any) {
      console.error('Error deleting alert:', error);
      throw error;
    }
  }, []);

  // Live updates with Server-Sent Events
  const setupLiveUpdates = useCallback(() => {
    if (!enableLiveUpdates) return;

    const eventSource = alertsService.createEventSource();
    if (!eventSource) return;

    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('Alerts Tab: Live update received:', data);

        if (!isMountedRef.current) return;

        switch (data.type) {
          case 'alert_triggered':
          case 'alert_updated':
            setAlerts(prev => {
              const existingIndex = prev.findIndex(alert => alert.id === data.alert.id);
              if (existingIndex >= 0) {
                const updated = [...prev];
                updated[existingIndex] = data.alert;
                return updated;
              } else {
                return [data.alert, ...prev];
              }
            });
            break;
          case 'alert_resolved':
            setAlerts(prev => prev.map(alert => 
              alert.id === data.alertId 
                ? { ...alert, state: 'ok', lastResolved: new Date().toISOString() }
                : alert
            ));
            break;
          case 'alert_deleted':
            setAlerts(prev => prev.filter(alert => alert.id !== data.alertId));
            break;
          case 'overview_updated':
            setOverview(data.overview);
            break;
        }
      } catch (err) {
        console.error('Error processing live update:', err);
      }
    };

    eventSource.onopen = () => {
      reconnectAttemptsRef.current = 0; // reset on successful connection
    };

    eventSource.onerror = (error) => {
      console.error('EventSource error:', error);
      reconnectAttemptsRef.current++;
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        setTimeout(() => {
          if (isMountedRef.current) {
            setupLiveUpdates();
          }
        }, 5000);
      } else {
        console.error(`EventSource: Max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}) reached, live updates disabled.`);
      }
    };

  }, [enableLiveUpdates]);

  // Initial data fetch
  useEffect(() => {
    console.log('Alerts Tab: useEffect triggered, starting initial fetch');
    
    isMountedRef.current = true;
    setLoading(true);
    
    fetchData().finally(() => {
      if (isMountedRef.current) {
        scheduleRefresh();
        setupLiveUpdates();
      }
    });

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [fetchData, scheduleRefresh, setupLiveUpdates]);

  // Refetch when filters change
  useEffect(() => {
    if (isMountedRef.current) {
      fetchData();
    }
  }, [fetchData]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  return {
    alerts,
    overview,
    notificationChannels,
    routingRules,
    maintenanceWindows,
    loading,
    error,
    filters,
    setFilters,
    refetch,
    lastUpdated,
    acknowledgeAlert,
    suppressAlert,
    deleteAlert
  };
};